import { useEffect, useState } from 'react';
import { apiGet } from '../lib/api';

export interface WebsitePageContent {
  page_key: string;
  title: string;
  summary?: string;
  content?: Record<string, any>;
  visible?: boolean;
  updated_at?: string;
}

interface WebsiteContentResponse {
  success: boolean;
  settings: Record<string, any>;
  pages: Record<string, WebsitePageContent>;
}

const cache: {
  data?: WebsiteContentResponse;
  promise?: Promise<WebsiteContentResponse>;
} = {};

export function useWebsiteContent(pageKey?: string) {
  const [data, setData] = useState<WebsiteContentResponse | undefined>(cache.data);
  const [loading, setLoading] = useState(!cache.data);

  useEffect(() => {
    let alive = true;

    if (!cache.promise) {
      cache.promise = apiGet<WebsiteContentResponse>('/api/public/website-content')
        .then((response) => {
          cache.data = response;
          return response;
        })
        .catch(() => {
          cache.promise = undefined;
          return { success: false, settings: {}, pages: {} };
        });
    }

    cache.promise.then((response) => {
      if (alive) {
        setData(response);
        setLoading(false);
      }
    });

    return () => {
      alive = false;
    };
  }, []);

  return {
    settings: data?.settings || {},
    page: pageKey ? data?.pages?.[pageKey] : undefined,
    pages: data?.pages || {},
    loading,
  };
}

/**
 * Copy that must never come back through a CMS override: the pre-repositioning
 * marketplace-first and single-crop messaging.
 */
const BLOCKED_CONTENT =
  /teen mother|young women\. land|rwandan produce|regenerative agriculture|pilot cohort|traceable avocado|avocado value chain|pmvp|funding-ready|secure setup link|see traceability/i;

export function isBlockedContent(value: unknown): boolean {
  return typeof value === 'string' && BLOCKED_CONTENT.test(value);
}

export function contentText(page: WebsitePageContent | undefined, key: string, fallback: string) {
  const value = page?.content?.[key];
  if (typeof value !== 'string' || !value.trim()) return fallback;
  return isBlockedContent(value) ? fallback : value;
}

export function contentList<T extends readonly unknown[]>(page: WebsitePageContent | undefined, key: string, fallback: T): T {
  const value = page?.content?.[key];
  if (!Array.isArray(value) || !value.length) return fallback;
  return value.some(isBlockedContent) ? fallback : (value as unknown as T);
}
